import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Camera } from "lucide-react";

import { Preloader } from "@/components";
import { tg } from "@/constants/telegram";
import { useGetUserMutation } from "@/store/usersApi";
import { useGetExercizeByTrainingMutation, useAddExercizeResultMutation, useSetExercizeMaxWeightMutation } from "@/store/trainingsApi";
import { VideoAnalysisModal } from "@/features/video-analysis-modal";

import styles from "./WorkoutExercisePage.module.scss";

type ApproachConfig = {
  approach_num: number;
  repetitions: number;
  repetitions_margin: number; // повторы в запасе
  weight: number | null; // процент от максимального веса или null если без веса
  no_weight: boolean; // true если упражнение без веса
};

type LastResultItem = {
  approach_number: number;
  schedule_id: number;
  repeats: number;
  weight: number | null;
};

type ExerciseData = {
  id: number;
  title: string;
  videoUrl: string;
  sets: number;
  currentSet: number;
  duration: number; // в секундах
  config: ApproachConfig[];
  lastResult?: LastResultItem[];
  userMaxWeight: number | null;
};

export const WorkoutExercisePage = () => {
  const { trainingId, exerciseId } = useParams<{ trainingId: string; exerciseId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const videoRef = useRef<HTMLVideoElement>(null);
  

  const [isLoading, setIsLoading] = useState(true);
  const [exercise, setExercise] = useState<ExerciseData | null>(null);
  const [initData, setInitData] = useState<string | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [nextExercizeId, setNextExercizeId] = useState<number | null>(null);
  
  // Получаем tse и prevExercizeId из URL параметров
  const tseParam = searchParams.get('tse');
  const tse = tseParam !== null ? Number(tseParam) || 0 : 0;
  const prevExercizeIdParam = searchParams.get('prevExercizeId');
  const urlPrevExercizeId = prevExercizeIdParam !== null ? Number(prevExercizeIdParam) || null : null;
  const [prevExercizeId, setPrevExercizeId] = useState<number | null>(urlPrevExercizeId);

  const [getUser] = useGetUserMutation();
  const [getExercizeByTraining] = useGetExercizeByTrainingMutation();
  const [addExercizeResult] = useAddExercizeResultMutation();
  const [setExercizeMaxWeight] = useSetExercizeMaxWeightMutation();
  
  // Состояния для управления
  const [isTimerRunning, setIsTimerRunning] = useState(false); // для таймера
  const [isVideoPlaying, setIsVideoPlaying] = useState(false); // для видео
  const [currentTime, setCurrentTime] = useState(37); // таймер
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [breakDuration, setBreakDuration] = useState(60); // время отдыха между подходами в секундах
  const [isRestMode, setIsRestMode] = useState(false); // режим отдыха
  
  // Модальные окна
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEndConfirmModalOpen, setIsEndConfirmModalOpen] = useState(false);
  const [isDoneModalOpen, setIsDoneModalOpen] = useState(false);
  const [editedConfig, setEditedConfig] = useState<ApproachConfig[]>([]);
  const [editingApproachIndex, setEditingApproachIndex] = useState<number | null>(null);
  
  // Состояние для модального окна максимального веса
  const [isMaxWeightModalOpen, setIsMaxWeightModalOpen] = useState(false);
  const [maxWeightInput, setMaxWeightInput] = useState('');

  // Состояние для модального окна анализа техники
  const [isVideoAnalysisModalOpen, setIsVideoAnalysisModalOpen] = useState(false);

  // Состояние для отслеживания выполненных подходов в текущей сессии
  const [completedApproachesInSession, setCompletedApproachesInSession] = useState<Set<number>>(new Set());

  useEffect(() => {
    tg.ready();
    window.scrollTo(0, 0);
    const data = tg.initData || null;
    setInitData(data);
    
    // Предотвращаем горизонтальный скролл
    const originalOverflowX = document.body.style.overflowX;
    document.body.style.overflowX = 'hidden';
    
    return () => {
      document.body.style.overflowX = originalOverflowX;
    };
  }, []);

  useEffect(() => {
    const fetchExerciseData = async () => {
      if (!trainingId || !exerciseId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        await getUser().unwrap();

        // Определяем тип тренировки из query параметра или используем 'schedule' по умолчанию
        const trainingType = (searchParams.get('type') as 'free' | 'schedule') || 'schedule';

        // Получаем данные упражнения
        const response = await getExercizeByTraining({
          training_id: Number(trainingId),
          exercize_id: Number(exerciseId),
          training_type: trainingType,
          tse: tse,
        }).unwrap();

        // Проверяем, что данные получены
        if (!response || !response.exercize || !response.approaches || response.approaches.length === 0) {
          console.error("Неполные данные упражнения:", response);
          setIsLoading(false);
          return;
        }

        // Сохраняем next_exercize_id и prev_exercize_id для перехода между упражнениями
        setNextExercizeId(response.next_exercize_id ?? null);
        // Используем prev_exercize_id из ответа, если он есть, иначе из URL параметров
        const newPrevId = response.prev_exercize_id ?? urlPrevExercizeId;
        setPrevExercizeId(newPrevId);
        // Сохраняем break_duration из ответа API
        setBreakDuration(response.break_duration || 60);
        console.log("Exercise IDs:", {
          current: exerciseId,
          prev: newPrevId,
          next: response.next_exercize_id,
          tse: tse
        });

        // Преобразуем данные из API в формат компонента
        let videoUrl = response.exercize.video_url || '';
        console.log("Получен URL видео:", videoUrl);
        
        // Проверяем и обрабатываем URL видео для S3
        if (videoUrl) {
          // Убеждаемся, что URL полный и валидный
          if (!videoUrl.startsWith('http://') && !videoUrl.startsWith('https://')) {
            console.warn("Относительный URL видео:", videoUrl);
            // Если это относительный путь, возможно нужно добавить базовый URL
            // Но обычно S3 возвращает полные URL
          }
          
          // Для S3 URL убеждаемся, что он правильно закодирован
          try {
            new URL(videoUrl);
            console.log("URL видео валиден:", videoUrl);
          } catch (e) {
            console.error("Некорректный URL видео:", videoUrl, e);
            videoUrl = ''; // Сбрасываем если URL невалиден
          }
        }
        
        // Определяем текущий подход на основе выполненных результатов
        let initialCurrentSet = 1;
        if (response.lastResult && response.lastResult.length > 0 && trainingId) {
          // Фильтруем результаты для текущей тренировки
          const currentTrainingResults = response.lastResult.filter(
            (item) => item.schedule_id === Number(trainingId)
          );
          
          if (currentTrainingResults.length > 0) {
            // Находим максимальный номер выполненного подхода
            const maxCompletedApproach = Math.max(
              ...currentTrainingResults.map((item) => item.approach_number)
            );
            const totalSets = response.approaches.length;
            
            // Если все подходы выполнены, ставим последний, иначе следующий
            if (maxCompletedApproach >= totalSets) {
              initialCurrentSet = totalSets;
            } else {
              initialCurrentSet = maxCompletedApproach + 1;
            }
          }
        }
        
        const exerciseData: ExerciseData = {
          id: Number(exerciseId),
          title: response.exercize.title || 'Упражнение',
          videoUrl: videoUrl,
          sets: response.approaches.length,
          currentSet: initialCurrentSet,
          duration: response.approaches[0]?.duration || 60,
          config: response.approaches.map((approach) => ({
            approach_num: approach.approach_number || 1,
            repetitions: approach.repetitions || 0,
            repetitions_margin: approach.repetitions_margin || 0,
            weight: approach.procent_of_maximum_weight ?? null,
            no_weight: approach.procent_of_maximum_weight === null || approach.procent_of_maximum_weight === 0,
          })),
          lastResult: response.lastResult || undefined,
          userMaxWeight: response.user_max_weight ?? null,
        };

        setExercise(exerciseData);
        setCurrentTime(exerciseData.duration);
        // Сбрасываем состояние видео при загрузке нового упражнения
        setVideoCurrentTime(0);
        setVideoProgress(0);
        setIsVideoPlaying(false);
        setIsTimerRunning(false);
        setIsRestMode(false); // Сбрасываем режим отдыха при загрузке нового упражнения
        // Инициализируем выполненные подходы из результатов
        if (response.lastResult && response.lastResult.length > 0 && trainingId) {
          const currentTrainingResults = response.lastResult.filter(
            (item) => item.schedule_id === Number(trainingId)
          );
          setCompletedApproachesInSession(new Set(currentTrainingResults.map((item) => item.approach_number)));
        } else {
          setCompletedApproachesInSession(new Set());
        }
      } catch (err: any) {
        console.error("Ошибка при загрузке упражнения:", err);
        console.error("Детали ошибки:", {
          message: err?.message,
          data: err?.data,
          status: err?.status,
          initData: !!initData,
          trainingId,
          exerciseId,
        });
        setExercise(null);
      } finally {
        setIsLoading(false);
      }
    };

    if (trainingId && exerciseId) {
      fetchExerciseData();
    } else {
      console.log("Ожидание данных:", { trainingId, exerciseId });
      if (!trainingId || !exerciseId) {
        setIsLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trainingId, exerciseId, tse]);

  // Управление таймером - работает независимо от видео
  useEffect(() => {
    if (!isTimerRunning || currentTime <= 0) return;

    const timer = setInterval(() => {
      setCurrentTime((prevTime) => {
        if (prevTime <= 1) {
          setIsTimerRunning(false);
          // Если таймер закончился в режиме отдыха, выходим из режима отдыха
          setIsRestMode((prevRestMode) => {
            if (prevRestMode) {
              // Переходим к следующему подходу
              setExercise((prevExercise) => {
                if (prevExercise && prevExercise.currentSet < prevExercise.sets) {
                  setCurrentTime(prevExercise.duration);
                  return {
                    ...prevExercise,
                    currentSet: prevExercise.currentSet + 1,
                  };
                }
                return prevExercise;
              });
              return false;
            }
            return prevRestMode;
          });
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isTimerRunning, currentTime]);

  // Управление видео
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    
    // Сбрасываем прогресс при смене видео
    setVideoProgress(0);
    setVideoCurrentTime(0);

    const handleTimeUpdate = () => {
      if (!video) return;
      const currentTime = video.currentTime;
      const duration = video.duration;
      if (!isNaN(currentTime) && isFinite(currentTime) && currentTime >= 0) {
        setVideoCurrentTime(currentTime);
        if (duration && !isNaN(duration) && duration > 0 && isFinite(duration)) {
          const progress = Math.min(100, Math.max(0, (currentTime / duration) * 100));
          setVideoProgress(progress);
        } else if (duration === 0 || isNaN(duration)) {
          // Если duration еще не загружен, сбрасываем прогресс
          setVideoProgress(0);
        }
      }
    };

    const handleLoadedMetadata = () => {
      const duration = video.duration;
      if (duration && !isNaN(duration) && duration > 0 && isFinite(duration)) {
        setVideoDuration(duration);
        const currentTime = video.currentTime || 0;
        if (!isNaN(currentTime) && isFinite(currentTime) && currentTime >= 0) {
          const progress = Math.min(100, Math.max(0, (currentTime / duration) * 100));
          setVideoProgress(progress);
        } else {
          setVideoProgress(0);
        }
      }
    };

    const handlePlay = () => {
      // Не обновляем isVideoPlaying, чтобы кнопка не менялась
    };

    const handlePause = () => {
      // Не обновляем isVideoPlaying, чтобы кнопка не менялась
    };

    const handleLoadedData = () => {
      if (!video) return;
      const currentTime = video.currentTime || 0;
      const duration = video.duration;
      if (duration && !isNaN(duration) && duration > 0 && isFinite(duration)) {
        setVideoDuration(duration);
        const progress = Math.min(100, Math.max(0, (currentTime / duration) * 100));
        setVideoProgress(progress);
      }
      if (!isNaN(currentTime) && isFinite(currentTime) && currentTime >= 0) {
        setVideoCurrentTime(currentTime);
      }
    };

    const handleCanPlay = () => {
      if (!video) return;
      const currentTime = video.currentTime || 0;
      const duration = video.duration;
      if (duration && !isNaN(duration) && duration > 0 && isFinite(duration)) {
        setVideoDuration(duration);
        const progress = Math.min(100, Math.max(0, (currentTime / duration) * 100));
        setVideoProgress(progress);
      }
      if (!isNaN(currentTime) && isFinite(currentTime) && currentTime >= 0) {
        setVideoCurrentTime(currentTime);
      }
    };

    const handleProgress = () => {
      if (!video) return;
      const currentTime = video.currentTime;
      const duration = video.duration;
      if (!isNaN(currentTime) && isFinite(currentTime) && currentTime >= 0) {
        setVideoCurrentTime(currentTime);
        if (duration && !isNaN(duration) && duration > 0 && isFinite(duration)) {
          setVideoDuration(duration);
          const progress = Math.min(100, Math.max(0, (currentTime / duration) * 100));
          setVideoProgress(progress);
        }
      }
    };

    const handleSeeked = () => {
      if (!video) return;
      const currentTime = video.currentTime;
      const duration = video.duration;
      if (!isNaN(currentTime) && isFinite(currentTime) && currentTime >= 0) {
        setVideoCurrentTime(currentTime);
        if (duration && !isNaN(duration) && duration > 0 && isFinite(duration)) {
          const progress = Math.min(100, Math.max(0, (currentTime / duration) * 100));
          setVideoProgress(progress);
        }
      }
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("loadeddata", handleLoadedData);
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("progress", handleProgress);
    video.addEventListener("seeked", handleSeeked);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);

    // Проверяем начальное состояние и обновляем время
    if (video.readyState >= 2) {
      handleLoadedMetadata();
      const currentTime = video.currentTime;
      const duration = video.duration;
      setVideoCurrentTime(currentTime);
      if (duration && !isNaN(duration) && duration > 0 && isFinite(duration)) {
        const progress = Math.min(100, Math.max(0, (currentTime / duration) * 100));
        setVideoProgress(progress);
      }
    }

    // Периодически обновляем время и прогресс для надежности (каждые 100мс)
    const timeUpdateInterval = setInterval(() => {
      if (video && video.readyState >= 2) {
        const currentTime = video.currentTime;
        const duration = video.duration;
        if (!isNaN(currentTime) && isFinite(currentTime) && currentTime >= 0) {
          setVideoCurrentTime(currentTime);
          if (duration && !isNaN(duration) && duration > 0 && isFinite(duration)) {
            const progress = Math.min(100, Math.max(0, (currentTime / duration) * 100));
            setVideoProgress(progress);
          }
        }
      }
    }, 100);

    return () => {
      clearInterval(timeUpdateInterval);
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("loadeddata", handleLoadedData);
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("progress", handleProgress);
      video.removeEventListener("seeked", handleSeeked);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
    };
  }, [exercise?.videoUrl]);

  const handlePlayPause = () => {
    // Кнопка Play/Pause управляет только таймером, видео управляется отдельно
    setIsTimerRunning(!isTimerRunning);
  };

  const handleRestart = () => {
    if (isRestMode) {
      setCurrentTime(breakDuration);
    } else {
      setCurrentTime(exercise?.duration || 60);
    }
    const video = videoRef.current;
    if (video) {
      video.currentTime = 0;
    }
  };

  const handlePrevExercise = () => {
    console.log("handlePrevExercise called", { trainingId, prevExercizeId, tse });
    
    if (!trainingId) {
      console.log("No trainingId, navigating back");
      navigate(-1);
      return;
    }
    
    if (prevExercizeId !== null && prevExercizeId !== undefined) {
      const trainingType = (searchParams.get('type') as 'free' | 'schedule') || 'schedule';
      const newTse = Math.max(0, tse - 1); // Не даем tse уйти в отрицательные значения
      // Получаем prevExercizeId для предыдущего упражнения из текущего exerciseId
      const prevPrevId = exerciseId; // Текущее упражнение станет prev для предыдущего
      const trainingTypeParam = trainingType ? `&type=${trainingType}` : '';
      const newPath = `/workout-exercise/${trainingId}/${prevExercizeId}?tse=${newTse}&prevExercizeId=${prevPrevId}${trainingTypeParam}`;
      console.log("Navigating to previous exercise:", newPath);
      navigate(newPath);
    } else {
      // Если нет предыдущего упражнения, возвращаемся к списку упражнений
      const trainingType = (searchParams.get('type') as 'free' | 'schedule') || 'schedule';
      const trainingTypeParam = trainingType ? `?type=${trainingType}` : '';
      const newPath = `/workout-preview/${trainingId}${trainingTypeParam}`;
      console.log("No previous exercise, navigating to preview:", newPath);
      navigate(newPath);
    }
  };

  const handleNextExercise = () => {
    if (!trainingId || !nextExercizeId) return;
    
    const trainingType = (searchParams.get('type') as 'free' | 'schedule') || 'schedule';
    const newTse = tse + 1;
    // Текущее упражнение станет prev для следующего
    const prevId = exerciseId;
    const trainingTypeParam = trainingType ? `&type=${trainingType}` : '';
    navigate(`/workout-exercise/${trainingId}/${nextExercizeId}?tse=${newTse}&prevExercizeId=${prevId}${trainingTypeParam}`);
  };

  const handleEnd = () => {
    // Показываем модальное окно подтверждения
    setIsEndConfirmModalOpen(true);
  };

  const handleEndConfirm = () => {
    // Останавливаем только таймер, видео не трогаем
    setIsTimerRunning(false);
    setIsEndConfirmModalOpen(false);
    // Возврат к списку упражнений
    const trainingType = (searchParams.get('type') as 'free' | 'schedule') || 'schedule';
    const trainingTypeParam = trainingType ? `?type=${trainingType}` : '';
    navigate(`/workout-preview/${trainingId}${trainingTypeParam}`);
  };

  const handleDone = () => {
    if (!exercise || !initData || !trainingId || !exerciseId) return;
    
    // Если мы в режиме отдыха, пропускаем отдых и переходим к следующему подходу
    if (isRestMode) {
      setIsRestMode(false);
      setIsTimerRunning(false);
      if (exercise.currentSet < exercise.sets) {
        // Переход к следующему сету
        setExercise({
          ...exercise,
          currentSet: exercise.currentSet + 1,
        });
        setCurrentTime(exercise.duration);
      } else {
        // Упражнение завершено, переходим к следующему упражнению или обратно к списку
        const trainingType = (searchParams.get('type') as 'free' | 'schedule') || 'schedule';
        if (nextExercizeId !== null && nextExercizeId !== undefined) {
          // Переходим на следующее упражнение с увеличенным tse
          // Текущее упражнение станет prev для следующего
          const prevId = exerciseId;
          const trainingTypeParam = trainingType ? `&type=${trainingType}` : '';
          navigate(`/workout-exercise/${trainingId}/${nextExercizeId}?tse=${tse + 1}&prevExercizeId=${prevId}${trainingTypeParam}`);
        } else {
          // Возвращаемся к списку упражнений
          const trainingTypeParam = trainingType ? `?type=${trainingType}` : '';
          navigate(`/workout-preview/${trainingId}${trainingTypeParam}`);
        }
      }
      return;
    }
    
    // Открываем модальное окно для ввода параметров подхода
    const currentApproach = exercise.config[exercise.currentSet - 1];
    if (!currentApproach) return;
    
    // Инициализируем данные для модального окна с текущими значениями
    setEditedConfig([{ ...currentApproach }]);
    setEditingApproachIndex(exercise.currentSet - 1);
    setIsDoneModalOpen(true);
  };

  const handleDoneConfirm = async () => {
    if (!exercise || !initData || !trainingId || !exerciseId || editedConfig.length === 0) return;
    
    const currentApproach = editedConfig[0];
    const approachIndex = exercise.currentSet - 1;
    
    // Обновляем конфигурацию подхода
    const newConfig = [...exercise.config];
    newConfig[approachIndex] = currentApproach;
    setExercise({ ...exercise, config: newConfig });
    
    // Отправляем выполненный подход
    try {
      await addExercizeResult({
        training_id: Number(trainingId),
        exercize_id: Number(exerciseId),
        approachs: [{
          approach_number: currentApproach.approach_num,
          repetitions: currentApproach.repetitions,
          weight: currentApproach.weight || 0,
        }],
      }).unwrap();

      // Помечаем подход как выполненный
      setCompletedApproachesInSession(prev => new Set(prev).add(currentApproach.approach_num));
    } catch (err) {
      console.error("Ошибка при сохранении результатов подхода:", err);
      alert('Ошибка сохранения результата. Проверьте соединение и попробуйте снова.');
      return; // Не закрываем модал — пользователь должен повторить
    }

    // Закрываем модальное окно
    setIsDoneModalOpen(false);
    setEditingApproachIndex(null);
    
    if (exercise.currentSet < exercise.sets) {
      // Переход в режим отдыха перед следующим подходом
      setIsRestMode(true);
      setCurrentTime(breakDuration);
      setIsTimerRunning(true); // Автоматически запускаем таймер отдыха
    } else {
      // Упражнение завершено, переходим к следующему упражнению или обратно к списку
      const trainingType = (searchParams.get('type') as 'free' | 'schedule') || 'schedule';
      if (nextExercizeId !== null && nextExercizeId !== undefined) {
        // Переходим на следующее упражнение с увеличенным tse
        // Текущее упражнение станет prev для следующего
        const prevId = exerciseId;
        const trainingTypeParam = trainingType ? `&type=${trainingType}` : '';
        navigate(`/workout-exercise/${trainingId}/${nextExercizeId}?tse=${tse + 1}&prevExercizeId=${prevId}${trainingTypeParam}`);
      } else {
        // Возвращаемся к списку упражнений
        const trainingTypeParam = trainingType ? `?type=${trainingType}` : '';
        navigate(`/workout-preview/${trainingId}${trainingTypeParam}`);
      }
    }
  };

  const toggleFullscreen = async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      // Проверяем, находится ли видео в fullscreen
      const isCurrentlyFullscreen = 
        document.fullscreenElement === video ||
        (document as any).webkitFullscreenElement === video ||
        (document as any).mozFullScreenElement === video ||
        (document as any).msFullscreenElement === video;

      if (isCurrentlyFullscreen) {
        // Выход из fullscreen
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          (document as any).webkitExitFullscreen();
        } else if ((document as any).mozCancelFullScreen) {
          (document as any).mozCancelFullScreen();
        } else if ((document as any).msExitFullscreen) {
          (document as any).msExitFullscreen();
        }
        setIsFullscreen(false);
      } else {
        // Вход в fullscreen - применяем к видео элементу
        // Для iOS используем встроенный fullscreen видео
        if ((video as any).webkitEnterFullscreen) {
          // iOS Safari - использует нативный fullscreen плеер
          (video as any).webkitEnterFullscreen();
          setIsFullscreen(true);
        } else if ((video as any).webkitRequestFullscreen) {
          // Safari на десктопе
          await (video as any).webkitRequestFullscreen();
          setIsFullscreen(true);
        } else if (video.requestFullscreen) {
          // Стандартный API - применяем к видео
          await video.requestFullscreen();
          setIsFullscreen(true);
        } else if ((video as any).mozRequestFullScreen) {
          // Firefox
          await (video as any).mozRequestFullScreen();
          setIsFullscreen(true);
        } else if ((video as any).msRequestFullscreen) {
          // IE/Edge
          await (video as any).msRequestFullscreen();
          setIsFullscreen(true);
        }
      }
    } catch (error) {
      console.error("Fullscreen error:", error);
    }
  };

  // Отслеживание изменения fullscreen режима
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement === video ||
        (document as any).webkitFullscreenElement === video ||
        (document as any).mozFullScreenElement === video ||
        (document as any).msFullscreenElement === video
      );
      setIsFullscreen(isCurrentlyFullscreen);
    };

    // Слушаем события fullscreen на документе
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    // Для iOS - слушаем события на самом видео
    video.addEventListener("webkitbeginfullscreen", () => setIsFullscreen(true));
    video.addEventListener("webkitendfullscreen", () => setIsFullscreen(false));

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
      document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
      
      video.removeEventListener("webkitbeginfullscreen", () => setIsFullscreen(true));
      video.removeEventListener("webkitendfullscreen", () => setIsFullscreen(false));
    };
  }, []);

  const formatWeight = (weightPct: number | null, userMaxWeight: number | null): string => {
    if (weightPct === null || weightPct === 0) return 'Без веса';
    if (userMaxWeight && userMaxWeight > 0) {
      const actualKg = Math.round(userMaxWeight * weightPct / 100 * 10) / 10;
      return `${actualKg} кг (${weightPct}% МВ)`;
    }
    return `${weightPct}% МВ`;
  };

  const getSetTypeLabel = (weightPct: number | null, allApproaches: ApproachConfig[]): string | null => {
    if (!weightPct || weightPct === 0) return null;
    const maxPct = Math.max(...allApproaches.map(a => a.weight || 0));
    if (maxPct === 0) return null;
    const ratio = weightPct / maxPct;
    if (ratio < 0.55) return 'Разминка';
    if (ratio < 0.80) return 'Подводящий';
    return 'Рабочий';
  };

  const handleSaveMaxWeight = async () => {
    const kg = parseFloat(maxWeightInput.replace(',', '.'));
    if (isNaN(kg) || kg <= 0) return;
    try {
      await setExercizeMaxWeight({ exercize_id: Number(exerciseId), weight: kg }).unwrap();
      setIsMaxWeightModalOpen(false);
      setMaxWeightInput('');
      if (initData && trainingId && exerciseId) {
        setIsLoading(true);
        const trainingType = (searchParams.get('type') as 'free' | 'schedule') || 'schedule';
        const response = await getExercizeByTraining({
          training_id: Number(trainingId),
          exercize_id: Number(exerciseId),
          training_type: trainingType,
          tse: tse,
        }).unwrap();
        if (response?.approaches?.length > 0) {
          setExercise(prev => prev ? {
            ...prev,
            userMaxWeight: response.user_max_weight ?? null,
          } : prev);
        }
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Failed to save max weight:', err);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Функция для получения списка пройденных подходов из результатов текущей тренировки
  const getCompletedApproachesFromResults = (): Set<number> => {
    if (!exercise?.lastResult || !trainingId) {
      return new Set();
    }

    // Фильтруем результаты где schedule_id === training_id
    const currentTrainingResults = exercise.lastResult.filter(
      (item) => item.schedule_id === Number(trainingId)
    );

    // Возвращаем Set с номерами подходов, которые были пройдены
    return new Set(currentTrainingResults.map((item) => item.approach_number));
  };

  const formatLastResult = (lastResult?: LastResultItem[]): string => {
    if (!lastResult || lastResult.length === 0) {
      return "N/A";
    }

    // Группируем по schedule_id
    const grouped = lastResult.reduce((acc, item) => {
      if (!acc[item.schedule_id]) {
        acc[item.schedule_id] = [];
      }
      acc[item.schedule_id].push(item);
      return acc;
    }, {} as Record<number, LastResultItem[]>);

    // Форматируем каждую группу
    const formattedGroups = Object.entries(grouped)
      .sort(([a], [b]) => Number(b) - Number(a)) // Сортируем по schedule_id (новые первыми)
      .map(([scheduleId, items]) => {
        // Сортируем подходы по номеру подхода
        const sortedItems = items.sort((a, b) => a.approach_number - b.approach_number);
        const approaches = sortedItems.map(item => {
          let weightStr = '';
          if (item.weight !== null && item.weight !== undefined) {
            if (item.weight === 0) {
              weightStr = ' без веса';
            } else {
              weightStr = ` ${item.weight} кг`;
            }
          }
          return `${item.repeats}${weightStr}`;
        }).join(', ');
        return approaches;
      })
      .join(' | ');

    return formattedGroups || "N/A";
  };

  // Вычисляем прогресс таймера в зависимости от режима
  const timerMaxTime = isRestMode ? breakDuration : (exercise?.duration || 60);
  const progressPercentage = timerMaxTime > 0 ? ((currentTime / timerMaxTime) * 100) : 0;
  const circumference = 2 * Math.PI * 80; // радиус 80 (для компактного таймера тоже используется этот радиус)
  const strokeDashoffset = circumference - (progressPercentage / 100) * circumference;

  if (isLoading) {
    return <Preloader hasMenu={false} />;
  }

  if (!exercise) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <p style={{ marginBottom: '20px', color: '#ff3b30' }}>Не удалось загрузить упражнение</p>
        <button
          onClick={async () => {
            if (initData && trainingId && exerciseId) {
              setIsLoading(true);
              try {
                await getUser().unwrap();
                const trainingType = (searchParams.get('type') as 'free' | 'schedule') || 'schedule';
                const response = await getExercizeByTraining({
                  training_id: Number(trainingId),
                  exercize_id: Number(exerciseId),
                  training_type: trainingType,
                  tse: tse,
                }).unwrap();
                if (response?.exercize && response?.approaches?.length > 0) {
                  const exerciseData: ExerciseData = {
                    id: Number(exerciseId),
                    title: response.exercize.title || 'Упражнение',
                    videoUrl: response.exercize.video_url || '',
                    sets: response.approaches.length,
                    currentSet: 1,
                    duration: response.approaches[0]?.duration || 60,
                    userMaxWeight: response.user_max_weight ?? null,
                    config: response.approaches.map((approach) => ({
                      approach_num: approach.approach_number || 1,
                      repetitions: approach.repetitions || 0,
                      repetitions_margin: approach.repetitions_margin || 0,
                      weight: approach.procent_of_maximum_weight ?? null,
                      no_weight: approach.procent_of_maximum_weight === null || approach.procent_of_maximum_weight === 0,
                    })),
                  };
                  setNextExercizeId(response.next_exercize_id ?? null);
                  setBreakDuration(response.break_duration || 60);
                  setExercise(exerciseData);
                  setCurrentTime(exerciseData.duration);
                }
              } catch (err) {
                console.error('Retry failed:', err);
              } finally {
                setIsLoading(false);
              }
            }
          }}
          style={{ padding: '10px 20px', cursor: 'pointer', backgroundColor: '#007aff', color: '#fff', border: 'none', borderRadius: '10px', marginBottom: '12px', fontSize: '15px' }}
        >
          Попробовать снова
        </button>
        <button
          onClick={() => {
            const trainingType = (searchParams.get('type') as 'free' | 'schedule') || 'schedule';
            navigate(`/workout-preview/${trainingId}${trainingType ? `?type=${trainingType}` : ''}`);
          }}
          style={{ padding: '10px 20px', cursor: 'pointer', background: 'none', border: '1px solid #8e8e93', borderRadius: '10px', color: '#8e8e93', fontSize: '15px' }}
        >
          Назад к тренировке
        </button>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {/* Video Section */}
      <div className={styles.videoSection}>
        {/* Video Controls Header */}
        <div className={styles.videoControls}>
          <button 
            className={styles.backButton} 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handlePrevExercise();
            }}
            type="button"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          
          <div 
            className={styles.progressBar}
            onClick={(e) => {
              const video = videoRef.current;
              const progressBar = e.currentTarget;
              if (video && video.duration && !isNaN(video.duration) && video.duration > 0) {
                const rect = progressBar.getBoundingClientRect();
                const clickX = e.clientX - rect.left;
                const percentage = Math.max(0, Math.min(1, clickX / rect.width));
                const newTime = percentage * video.duration;
                video.currentTime = newTime;
                setVideoCurrentTime(newTime);
                setVideoProgress(percentage * 100);
              }
            }}
          >
            <div className={styles.progressFill} style={{ width: `${videoProgress}%` }} />
            <div 
              className={styles.progressHandle}
              style={{ left: `${videoProgress}%` }}
            />
          </div>
          
          <span className={styles.videoTime}>
            {formatTime(Math.floor(videoCurrentTime ?? 0))}
            {videoDuration > 0 && ` / ${formatTime(Math.floor(videoDuration))}`}
          </span>
          
          <button 
            className={styles.pauseButton} 
            onClick={() => {
              const video = videoRef.current;
              if (video) {
                if (video.paused) {
                  video.play();
                } else {
                  video.pause();
                }
              }
            }}
            title="Воспроизвести/Пауза видео"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M8 5V19L19 12L8 5Z" fill="white"/>
            </svg>
          </button>
          
          <button className={styles.endButton} onClick={handleEnd}>
            <div className={styles.endIcon} />
            END
          </button>
        </div>

        {/* Video Player */}
        {videoError ? (
          <div style={{ 
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center', 
            alignItems: 'center',
            backgroundColor: '#000',
            color: '#fff',
            padding: '20px'
          }}>
            <p style={{ marginBottom: '10px' }}>Ошибка загрузки видео</p>
            <p style={{ fontSize: '12px', opacity: 0.7, marginBottom: '10px' }}>{videoError}</p>
            <button 
              onClick={() => {
                setVideoError(null);
                const video = videoRef.current;
                if (video) {
                  video.load();
                }
              }}
              style={{ 
                padding: '10px 20px', 
                backgroundColor: '#fff', 
                color: '#000', 
                border: 'none', 
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              Попробовать снова
            </button>
          </div>
        ) : (
          <video
            ref={videoRef}
            className={styles.video}
            src={exercise.videoUrl}
            playsInline
            controlsList="nodownload"
            preload="auto"
            onError={(e) => {
              const error = e.currentTarget.error;
              let errorMessage = "Не удалось загрузить видео";
              if (error) {
                switch (error.code) {
                  case error.MEDIA_ERR_ABORTED:
                    errorMessage = "Загрузка видео прервана";
                    break;
                  case error.MEDIA_ERR_NETWORK:
                    errorMessage = "Ошибка сети при загрузке видео";
                    break;
                  case error.MEDIA_ERR_DECODE:
                    errorMessage = "Ошибка декодирования видео";
                    break;
                  case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
                    errorMessage = "Формат видео не поддерживается";
                    break;
                }
              }
              console.error("Ошибка загрузки видео:", errorMessage, error);
              console.error("URL видео:", exercise.videoUrl);
              setVideoError(errorMessage);
            }}
            onLoadStart={() => {
              console.log("Начало загрузки видео:", exercise.videoUrl);
              setVideoError(null);
            }}
            onLoadedData={() => {
              console.log("Видео загружено:", exercise.videoUrl);
              setVideoError(null);
            }}
            onCanPlay={() => {
              console.log("Видео готово к воспроизведению");
              setVideoError(null);
            }}
          />
        )}

        {/* Fullscreen Button */}
        <button className={styles.fullscreenButton} onClick={toggleFullscreen}>
          {isFullscreen ? (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M7 2V7H2" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              <path d="M13 18V13H18" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              <path d="M7 18V13H2" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              <path d="M13 2V7H18" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M2 7V2H7" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              <path d="M18 13V18H13" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              <path d="M2 13V18H7" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              <path d="M18 7V2H13" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          )}
        </button>
      </div>

      {/* Exercise Info - Компактная версия */}
      <div className={styles.exerciseInfo}>
        <div className={styles.exerciseHeader}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
            <h2 className={styles.exerciseTitle} style={{ flex: 1 }}>{exercise.title}</h2>
            <button
              onClick={() => setIsVideoAnalysisModalOpen(true)}
              title="Распознать технику"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                background: '#f2f2f7',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                color: '#1c1c1e',
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}
            >
              <Camera size={15} color="#8e8e93" />
              Техника
            </button>
          </div>
          <div className={styles.setsProgressCompact}>
            <span className={styles.setsLabelCompact}>{exercise.sets} подходов</span>
            <div className={styles.setsIndicatorsCompact}>
              {Array.from({ length: exercise.sets }).map((_, index) => (
                <div
                  key={index}
                  className={`${styles.setIndicatorCompact} ${
                    index < exercise.currentSet ? styles.setCompleted : ""
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Компактное отображение подходов в одну строку */}
        <div className={styles.approachesListCompact}>
          {exercise.config.map((approach, index) => {
            const completedApproaches = getCompletedApproachesFromResults();
            const isCompletedInResults = completedApproaches.has(approach.approach_num);
            const isCompletedInSession = completedApproachesInSession.has(approach.approach_num);
            const isCurrent = index === exercise.currentSet - 1;
            
            // Подход считается пройденным, если он пройден в текущей сессии ИЛИ есть в результатах тренировки
            // ИЛИ если идет отдых и это предыдущий подход
            const isApproachCompleted = isCompletedInSession || isCompletedInResults || 
              (isRestMode && index === exercise.currentSet - 2);
            
            return (
            <div 
              key={approach.approach_num} 
              className={`${styles.approachItemCompact} ${
                isApproachCompleted ? styles.approachCompleted : 
                isCurrent ? styles.approachCurrent : ''
              }`}
              onClick={async () => {
                setEditingApproachIndex(index);
                setEditedConfig([{ ...approach }]);
                setIsEditModalOpen(true);
              }}
              style={{ cursor: 'pointer' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                <div className={styles.approachNumberCompact}>
                  {isApproachCompleted ? '✓' : approach.approach_num}
                </div>
                <div className={styles.approachInfoCompact}>
                {(() => {
                  const label = getSetTypeLabel(approach.weight, exercise.config);
                  if (!label) return null;
                  const maxPct = Math.max(...exercise.config.map(a => a.weight || 0));
                  const ratio = maxPct > 0 ? (approach.weight || 0) / maxPct : 1;
                  const labelColor = ratio < 0.55 ? '#ff9500' : ratio < 0.80 ? '#007aff' : '#34c759';
                  return (
                    <span style={{ fontSize: '10px', fontWeight: '600', color: labelColor, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {label}
                    </span>
                  );
                })()}
                <div className={styles.approachInfoItem}>
                  <div className={styles.approachInfoItemContent}>
                    <span className={styles.approachInfoLabel}>Повторения:</span>
                    <span className={styles.approachReps}>{approach.repetitions}</span>
                  </div>
                </div>
                {approach.repetitions_margin > 0 && (
                  <div className={styles.approachInfoItem}>
                    <div className={styles.approachInfoItemContent}>
                      <span className={styles.approachInfoLabel}>В запасе (RIR):</span>
                      <span className={styles.approachMargin}>+{approach.repetitions_margin}</span>
                      <span className={styles.approachInfoHintText}>
                        (Сколько повторений у тебя должно остаться в запасе до отказа)
                      </span>
                    </div>
                  </div>
                )}
                {!approach.no_weight && approach.weight !== null && (
                  <div className={styles.approachInfoItem}>
                    <div className={styles.approachInfoItemContent}>
                      <span className={styles.approachInfoLabel}>Вес:</span>
                      <span className={styles.approachWeight}>{formatWeight(approach.weight, exercise.userMaxWeight)}</span>
                    </div>
                  </div>
                )}
                {approach.no_weight && (
                  <div className={styles.approachInfoItem}>
                    <div className={styles.approachInfoItemContent}>
                      <span className={styles.approachInfoLabel}>Вес:</span>
                      <span className={styles.approachWeight}>Без веса</span>
                    </div>
                  </div>
                )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      </div>

      {/* Кнопка установки максимального веса */}
      <div style={{ padding: '0 16px 12px' }}>
        <button
          onClick={() => {
            setMaxWeightInput(exercise.userMaxWeight ? String(exercise.userMaxWeight) : '');
            setIsMaxWeightModalOpen(true);
          }}
          style={{
            width: '100%',
            padding: '10px 16px',
            backgroundColor: '#f2f2f7',
            border: 'none',
            borderRadius: '10px',
            fontSize: '14px',
            color: '#1c1c1e',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          {exercise.userMaxWeight ? `Макс. вес: ${exercise.userMaxWeight} кг (изменить)` : 'Указать максимальный вес'}
        </button>
      </div>

      {/* History Section - Компактная версия */}
      <div className={styles.historySectionCompact}>
        <div className={styles.lastResultCompact} onClick={() => setIsHistoryModalOpen(true)}>
          <span className={styles.lastResultLabel}>История упражнения: </span>
          <span className={styles.lastResultValue}>{formatLastResult(exercise.lastResult)}</span>
        </div>
        <button className={styles.historyButtonCompact} onClick={() => setIsHistoryModalOpen(true)} title="История">
          <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
            <path d="M6 4L10 8L6 12" stroke="#8e8e93" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      {/* Фиксированный таймер над нижними кнопками */}
      <div className={styles.fixedTimerBottom}>
        <div className={styles.fixedTimerWrapper}>
          <div className={styles.fixedTimerContent}>
            <svg className={`${styles.fixedTimerCircle} ${isRestMode ? styles.restMode : ''}`} width="100" height="100" viewBox="0 0 200 200">
              <circle
                cx="100"
                cy="100"
                r="80"
                fill="none"
                stroke="#f0f0f0"
                strokeWidth="12"
              />
              <circle
                cx="100"
                cy="100"
                r="80"
                fill="none"
                stroke={isRestMode ? "url(#fixedRestGradient)" : "url(#fixedGradient)"}
                strokeWidth="12"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                transform="rotate(-90 100 100)"
              />
              <defs>
                <linearGradient id="fixedGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#ff6b7a" />
                  <stop offset="100%" stopColor="#ee3d4a" />
                </linearGradient>
                <linearGradient id="fixedRestGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#34c759" />
                  <stop offset="100%" stopColor="#30d158" />
                </linearGradient>
              </defs>
            </svg>
            <div className={`${styles.fixedTimerValue} ${isRestMode ? styles.timerValueRest : ''}`}>{currentTime}</div>
          </div>

          {/* Кнопки управления рядом с таймером */}
          <div className={styles.fixedTimerActions}>
            <button className={styles.actionButtonCompact} onClick={handleRestart} title="Restart">
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10C18 14.4183 14.4183 18 10 18C7.23858 18 4.78249 16.5239 3.5 14.25" stroke="#8e8e93" strokeWidth="2" strokeLinecap="round"/>
                <path d="M2 14V10H6" stroke="#8e8e93" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            <button 
              className={styles.actionButtonCompact} 
              onClick={() => {
                setEditingApproachIndex(null);
                setEditedConfig([...exercise.config]);
                setIsEditModalOpen(true);
              }}
              title="Редактировать все подходы"
            >
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M14.5 2.5L17.5 5.5L6.5 16.5H3.5V13.5L14.5 2.5Z" stroke="#8e8e93" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            <button className={styles.actionButtonCompact} onClick={handlePlayPause} title={isTimerRunning ? "Пауза таймера" : "Старт таймера"}>
              {isTimerRunning ? (
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <rect x="5" y="3" width="3" height="14" fill="#8e8e93" rx="1"/>
                  <rect x="12" y="3" width="3" height="14" fill="#8e8e93" rx="1"/>
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <path d="M5 3L5 17L16 10L5 3Z" fill="#8e8e93"/>
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className={styles.bottomNav}>
        <button 
          className={styles.prevButton} 
          onClick={handlePrevExercise}
          disabled={!prevExercizeId}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 12L6 8L10 4" stroke="#8e8e93" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <button className={styles.doneButton} onClick={handleDone}>
          <div className={styles.doneIcon}>
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <path d="M6 16L12 22L26 8" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <span className={styles.doneText}>Done</span>
        </button>

        <button 
          className={styles.nextButton} 
          onClick={handleNextExercise}
          disabled={!nextExercizeId}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M6 4L10 8L6 12" stroke="#8e8e93" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>


      {/* Modals */}
      {/* End Confirm Modal */}
      {isEndConfirmModalOpen && (
        <div className={styles.modal} onClick={() => setIsEndConfirmModalOpen(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Завершить упражнение?</h3>
              <button className={styles.closeButton} onClick={() => setIsEndConfirmModalOpen(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="#8e8e93" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '15px', color: '#1c1c1e', marginBottom: '12px', lineHeight: '1.5' }}>
                Вы уверены, что хотите завершить упражнение?
              </p>
              <p style={{ fontSize: '14px', color: '#ff3b30', marginBottom: '0', lineHeight: '1.5', fontWeight: '500' }}>
                ⚠️ Внимание: Данные о выполнении упражнения не будут сохранены!
              </p>
            </div>
            <div className={styles.modalActions}>
              <button 
                className={styles.cancelButton}
                onClick={() => setIsEndConfirmModalOpen(false)}
              >
                Отмена
              </button>
              <button 
                className={styles.saveButton}
                onClick={handleEndConfirm}
                style={{ backgroundColor: '#ff3b30' }}
              >
                Завершить
              </button>
            </div>
          </div>
        </div>
      )}

      {isHistoryModalOpen && (
        <div className={styles.modal} onClick={() => setIsHistoryModalOpen(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>История упражнения</h3>
              <button className={styles.closeButton} onClick={() => setIsHistoryModalOpen(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="#8e8e93" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div className={styles.historyContent}>
              {exercise.lastResult && exercise.lastResult.length > 0 ? (
                (() => {
                  // Группируем по schedule_id
                  const grouped = exercise.lastResult.reduce((acc, item) => {
                    if (!acc[item.schedule_id]) {
                      acc[item.schedule_id] = [];
                    }
                    acc[item.schedule_id].push(item);
                    return acc;
                  }, {} as Record<number, LastResultItem[]>);

                  return Object.entries(grouped)
                    .sort(([a], [b]) => Number(b) - Number(a)) // Сортируем по schedule_id (новые первыми)
                    .map(([scheduleId, items]) => {
                      const sortedItems = items.sort((a, b) => a.approach_number - b.approach_number);
                      return (
                        <div key={scheduleId} className={styles.historyGroup}>
                          <div className={styles.historyApproaches}>
                            {sortedItems.map((item, index) => (
                              <div key={index} className={styles.historyApproach}>
                                <span className={styles.historyApproachNumber}>Подход {item.approach_number}:</span>
                                <span className={styles.historyApproachValue}>
                                  {item.repeats} повторений
                                  {item.weight !== null && item.weight !== undefined && (
                                    item.weight === 0 ? ' × без веса' : ` × ${item.weight} кг`
                                  )}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })
                })()
              ) : (
                <p style={{ textAlign: 'center', color: '#8e8e93', padding: '20px' }}>
                  История результатов пуста
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {isEditModalOpen && editedConfig.length > 0 && (
        <div className={styles.modal} onClick={() => {
          setIsEditModalOpen(false);
          setEditingApproachIndex(null);
        }}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>
                {editingApproachIndex !== null 
                  ? `Редактировать подход ${editedConfig[0].approach_num}` 
                  : 'Редактировать упражнение'}
              </h3>
              <button className={styles.closeButton} onClick={() => {
                setIsEditModalOpen(false);
                setEditingApproachIndex(null);
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="#8e8e93" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div className={styles.editForm}>
              {editedConfig.map((approach, index) => (
                <div key={approach.approach_num} className={styles.approachItem}>
                  <div className={styles.approachHeader}>
                    <span className={styles.approachNumber}>Подход {approach.approach_num}</span>
                  </div>
                  <div className={styles.approachInputs}>
                    <label>
                      <span>Повторения</span>
                      <input
                        type="number"
                        value={approach.repetitions}
                        onChange={(e) => {
                          const newConfig = [...editedConfig];
                          newConfig[index].repetitions = parseInt(e.target.value) || 0;
                          setEditedConfig(newConfig);
                        }}
                        min="0"
                      />
                    </label>
                    <label>
                      <span>Повторы в запасе</span>
                      <input
                        type="number"
                        value={approach.repetitions_margin || 0}
                        onChange={(e) => {
                          const newConfig = [...editedConfig];
                          newConfig[index].repetitions_margin = parseInt(e.target.value) || 0;
                          setEditedConfig(newConfig);
                        }}
                        min="0"
                      />
                    </label>
                    <label>
                      <span>Вес (кг)</span>
                      <div className={styles.weightInputWrapper}>
                        <input
                          type="number"
                          value={approach.no_weight ? "" : approach.weight || ""}
                          onChange={(e) => {
                            const newConfig = [...editedConfig];
                            const value = parseFloat(e.target.value);
                            newConfig[index].weight = isNaN(value) ? null : value;
                            setEditedConfig(newConfig);
                          }}
                          min="0"
                          disabled={approach.no_weight}
                          placeholder="кг"
                          className={approach.no_weight ? styles.disabledInput : ""}
                        />
                        {!approach.no_weight && approach.weight !== null && (
                          <span className={styles.percentSymbol}>кг</span>
                        )}
                      </div>
                    </label>
                  </div>
                  <label className={styles.noWeightCheckbox}>
                    <input
                      type="checkbox"
                      checked={approach.no_weight}
                      onChange={(e) => {
                        const newConfig = [...editedConfig];
                        newConfig[index].no_weight = e.target.checked;
                        if (e.target.checked) {
                          newConfig[index].weight = null;
                        }
                        setEditedConfig(newConfig);
                      }}
                    />
                    <span>Без веса</span>
                  </label>
                </div>
              ))}
            </div>
            <div className={styles.modalActions}>
              <button 
                className={styles.cancelButton} 
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingApproachIndex(null);
                }}
              >
                Отмена
              </button>
              <button 
                className={styles.saveButton}
                onClick={async () => {
                  if (exercise) {
                    if (editingApproachIndex !== null) {
                      // Редактирование одного подхода
                      const newConfig = [...exercise.config];
                      newConfig[editingApproachIndex] = editedConfig[0];
                      setExercise({ ...exercise, config: newConfig });
                      
                      // Если редактируется текущий подход, автоматически помечаем его выполненным и начинаем отдых
                      const editedApproach = editedConfig[0];
                      if (editingApproachIndex === exercise.currentSet - 1 && !completedApproachesInSession.has(editedApproach.approach_num)) {
                        // Сохраняем подход
                        try {
                          const trainingType = (searchParams.get('type') as 'free' | 'schedule') || 'schedule';
                          await addExercizeResult({
                            training_id: Number(trainingId),
                            exercize_id: Number(exerciseId),
                            approachs: [{
                              approach_number: editedApproach.approach_num,
                              repetitions: editedApproach.repetitions,
                              weight: editedApproach.weight || 0,
                            }],
                          }).unwrap();
                          
                          // Помечаем подход как выполненный
                          setCompletedApproachesInSession(prev => new Set(prev).add(editedApproach.approach_num));
                          
                          // Начинаем отдых, если есть еще подходы
                          if (exercise.currentSet < exercise.sets) {
                            setIsRestMode(true);
                            setCurrentTime(breakDuration);
                            setIsTimerRunning(true);
                          }
                        } catch (err) {
                          console.error("Ошибка при сохранении результатов подхода:", err);
                        }
                      }
                    } else {
                      // Редактирование всех подходов
                      setExercise({ ...exercise, config: editedConfig });
                    }
                  }
                  setIsEditModalOpen(false);
                  setEditingApproachIndex(null);
                }}
              >
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Max Weight Modal */}
      {isMaxWeightModalOpen && (
        <div className={styles.modal} onClick={() => setIsMaxWeightModalOpen(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Максимальный вес</h3>
              <button className={styles.closeButton} onClick={() => setIsMaxWeightModalOpen(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="#8e8e93" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div className={styles.editForm}>
              <div className={styles.approachItem}>
                <div className={styles.approachInputs}>
                  <label>
                    <span>Максимальный вес (кг)</span>
                    <input
                      type="number"
                      value={maxWeightInput}
                      onChange={(e) => setMaxWeightInput(e.target.value)}
                      min="0"
                      placeholder="кг"
                      autoFocus
                    />
                  </label>
                </div>
                <p style={{ fontSize: '13px', color: '#8e8e93', marginTop: '8px', lineHeight: '1.4' }}>
                  Укажите ваш максимальный вес для этого упражнения. Плановые нагрузки будут отображаться в кг.
                </p>
              </div>
            </div>
            <div className={styles.modalActions}>
              <button className={styles.cancelButton} onClick={() => setIsMaxWeightModalOpen(false)}>
                Отмена
              </button>
              <button className={styles.saveButton} onClick={handleSaveMaxWeight}>
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Done Modal - для ввода параметров подхода */}
      {isDoneModalOpen && editedConfig.length > 0 && (
        <div className={styles.modal} onClick={() => {
          setIsDoneModalOpen(false);
          setEditingApproachIndex(null);
        }}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>
                Введите параметры подхода {editedConfig[0].approach_num}
              </h3>
              <button className={styles.closeButton} onClick={() => {
                setIsDoneModalOpen(false);
                setEditingApproachIndex(null);
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="#8e8e93" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div className={styles.editForm}>
              <div className={styles.approachItem}>
                <div className={styles.approachHeader}>
                  <span className={styles.approachNumber}>Подход {editedConfig[0].approach_num}</span>
                </div>
                <div className={styles.approachInputs}>
                  <label>
                    <span>Повторения</span>
                    <input
                      type="number"
                      value={editedConfig[0].repetitions}
                      onChange={(e) => {
                        const newConfig = [...editedConfig];
                        newConfig[0].repetitions = parseInt(e.target.value) || 0;
                        setEditedConfig(newConfig);
                      }}
                      min="0"
                    />
                  </label>
                  <label>
                    <span>Вес, который вы подняли (кг)</span>
                    <div className={styles.weightInputWrapper}>
                      <input
                        type="number"
                        value={editedConfig[0].no_weight ? "" : editedConfig[0].weight || ""}
                        onChange={(e) => {
                          const newConfig = [...editedConfig];
                          const value = parseFloat(e.target.value);
                          newConfig[0].weight = isNaN(value) ? null : value;
                          setEditedConfig(newConfig);
                        }}
                        min="0"
                        disabled={editedConfig[0].no_weight}
                        placeholder="кг"
                        className={editedConfig[0].no_weight ? styles.disabledInput : ""}
                      />
                      {!editedConfig[0].no_weight && editedConfig[0].weight !== null && (
                        <span className={styles.percentSymbol}>кг</span>
                      )}
                    </div>
                    {!editedConfig[0].no_weight && editedConfig[0].weight !== null && (
                      <small style={{ color: '#8e8e93', fontSize: '12px' }}>
                        Запланировано: {formatWeight(editedConfig[0].weight, exercise?.userMaxWeight ?? null)}
                      </small>
                    )}
                  </label>
                </div>
                <label className={styles.noWeightCheckbox}>
                  <input
                    type="checkbox"
                    checked={editedConfig[0].no_weight}
                    onChange={(e) => {
                      const newConfig = [...editedConfig];
                      newConfig[0].no_weight = e.target.checked;
                      if (e.target.checked) {
                        newConfig[0].weight = null;
                      }
                      setEditedConfig(newConfig);
                    }}
                  />
                  <span>Без веса</span>
                </label>
              </div>
            </div>
            <div className={styles.modalActions}>
              <button 
                className={styles.cancelButton} 
                onClick={() => {
                  setIsDoneModalOpen(false);
                  setEditingApproachIndex(null);
                }}
              >
                Отмена
              </button>
              <button
                className={styles.saveButton}
                onClick={handleDoneConfirm}
              >
                Подтвердить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Analysis Modal */}
      <VideoAnalysisModal
        isOpen={isVideoAnalysisModalOpen}
        onClose={() => setIsVideoAnalysisModalOpen(false)}
        scheduleId={Number(trainingId)}
        exerciseSampleId={Number(exerciseId)}
      />
    </div>
  );
};


